import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { size } from 'es-toolkit/compat';

import { getUserCredentialsResponse } from 'api/userCredentials';
import { UserCredentials, UserCredentialsPaginatedResponse } from 'interfaces/userCredentials';

const DEFAULT_PAGE_SIZE = 100;

export interface PaginatedUserCredentialsQueryParams {
  labId: string;
  page?: number;
  perPage?: number;
  ascending?: boolean;
  sortField?: string;
  listNucleaiUsers?: boolean;
  searchQuery?: string;
  studyId?: string;
  enabled?: boolean;
  queryKeyPrefix?: string;
}

export const getPaginatedUserCredentialsQueryKey = ({
  labId,
  page = 0,
  perPage,
  ascending = true,
  sortField = 'email',
  listNucleaiUsers = false,
  searchQuery = '',
  studyId,
  queryKeyPrefix = 'paginatedUsers',
}: Omit<PaginatedUserCredentialsQueryParams, 'enabled'>) => {
  return [queryKeyPrefix, labId, { page, perPage, ascending, sortField, listNucleaiUsers, searchQuery, studyId }];
};

export const usePaginatedUserCredentials = ({
  labId,
  page = 0,
  perPage,
  ascending = true,
  sortField = 'email',
  listNucleaiUsers = false,
  searchQuery = '',
  studyId,
  enabled = true,
  queryKeyPrefix = 'paginatedUsers',
}: PaginatedUserCredentialsQueryParams): UseQueryResult<UserCredentialsPaginatedResponse> => {
  const queryKey = getPaginatedUserCredentialsQueryKey({
    labId,
    page,
    perPage,
    ascending,
    sortField,
    listNucleaiUsers,
    searchQuery,
    studyId,
    queryKeyPrefix,
  });

  return useQuery(
    queryKey,
    () =>
      getUserCredentialsResponse(labId, {
        page,
        perPage,
        ascending,
        sortField,
        listNucleaiUsers,
        searchQuery,
        studyId,
      }),
    {
      enabled: Boolean(labId) && enabled,
    }
  );
};

export const getAllUserCredentials = async ({
  labId,
  perPage = DEFAULT_PAGE_SIZE,
  ascending = true,
  sortField = 'email',
  listNucleaiUsers = false,
  searchQuery = '',
  studyId,
  queryClient,
  queryKeyPrefix = 'paginatedUsers',
}: Omit<PaginatedUserCredentialsQueryParams, 'enabled' | 'page'> & {
  queryClient?: QueryClient;
}): Promise<UserCredentials[]> => {
  let page = 0;
  let total = 0;
  let aggregatedUsers: UserCredentials[] = [];

  const fetchPage = (currentPage: number) =>
    getUserCredentialsResponse(labId, {
      page: currentPage,
      perPage,
      ascending,
      sortField,
      listNucleaiUsers,
      searchQuery,
      studyId,
    });

  // Iteratively fetch pages until we've retrieved all users, aggregating results as we go.
  // First fetch will update `total` with the overall count, allowing us to determine when we've fetched all pages.
  do {
    const queryKey = getPaginatedUserCredentialsQueryKey({
      labId,
      page,
      perPage,
      ascending,
      sortField,
      listNucleaiUsers,
      searchQuery,
      studyId,
      queryKeyPrefix,
    });

    const currentPage = page;

    const response = queryClient
      ? await queryClient.fetchQuery<UserCredentialsPaginatedResponse>({
          queryKey,
          queryFn: () => fetchPage(currentPage),
        })
      : await fetchPage(currentPage);

    aggregatedUsers = [...aggregatedUsers, ...response.users];
    total = response.total;
    page += 1;
  } while (size(aggregatedUsers) < total);

  return aggregatedUsers;
};
